import { Point } from '@printu/geom';
import { lineCircleIntersection } from 'app/views/components/editor/tools/edit/text/cloud/intersections/lineCircle';
import { lineSegmentIntersection } from 'app/views/components/editor/tools/edit/text/cloud/intersections/lineSegment';
import { getRectSegments, isPointInRect, } from 'app/views/components/editor/tools/edit/text/cloud/rectSegments';
// transform center-tail ray to create tail base
const angle = (30 / 180) * Math.PI;
const Consts = {
    useDistortedProjector: true,
};
const rayLeftMtx = [
    Math.cos(-angle),
    Math.sin(-angle),
    -Math.sin(-angle),
    Math.cos(-angle),
];
const rayRightMtx = [
    Math.cos(angle),
    Math.sin(angle),
    -Math.sin(angle),
    Math.cos(angle),
];
const getSegmentLineIntersetion = (l1, l2) => {
    switch (l1.type) {
        case 'line': {
            const { start: s1, end: e1 } = l1;
            const { start: s2, end: e2 } = l2;
            const point = lineSegmentIntersection(new Point(s1), new Point(e1), new Point(s2), new Point(e2));
            return point;
        }
        case 'arc': {
            const { start: s2, end: e2 } = l2;
            const point = lineCircleIntersection(new Point(s2), new Point(e2), new Point(l1.center), l1.radius, l1.angleFrom, l1.angleTo);
            return point;
        }
        default:
            return null;
    }
};
const getRectIntersection = (rect, segment) => {
    let intersection = null;
    const segments = getRectSegments(rect);
    while (segments.length && !intersection) {
        const rsegment = segments.shift();
        if (rsegment) {
            intersection = getSegmentLineIntersetion(rsegment, segment);
        }
    }
    return intersection;
};
const getIntersectionDetails = (segments, line, type) => {
    for (let index = 0; index < segments.length; index += 1) {
        const segment = segments[index];
        const intersection = getSegmentLineIntersetion(segment, line);
        if (intersection) {
            return {
                segmentIndex: index,
                intersection,
                type,
            };
        }
    }
    return null;
};
const getSegmentIntersetionDivision = (rect, tail, lines) => {
    const segments = getRectSegments(rect);
    const [leftRay, rightRay] = lines;
    const lIntersection = getIntersectionDetails(segments, leftRay, 'start');
    const rIntersection = getIntersectionDetails(segments, rightRay, 'end');
    if (!lIntersection || !rIntersection) {
        return null;
    }
    const result = [];
    // if both intersect the same segment - solve it here by dividing the one segment
    if (lIntersection.segmentIndex === rIntersection.segmentIndex) {
        const intersectionSegmentIndex = lIntersection.segmentIndex;
        return segments.reduce((accum, segment, index) => {
            if (index === intersectionSegmentIndex) {
                const { intersection: leftRayIntersection } = lIntersection;
                const { intersection: rightRayIntersection } = rIntersection;
                switch (segment.type) {
                    case 'arc':
                        {
                            const { x: cx, y: cy } = segment.center;
                            // get intersection angles for both rays
                            const leftAngle = Math.atan2(leftRayIntersection.y - cy, leftRayIntersection.x - cx);
                            const rightAngle = Math.atan2(rightRayIntersection.y - cy, rightRayIntersection.x - cx);
                            accum.push(...[
                                {
                                    type: 'arc',
                                    center: Object.assign({}, segment.center),
                                    radius: segment.radius,
                                    angleFrom: segment.angleFrom,
                                    angleTo: leftAngle,
                                },
                                {
                                    type: 'line',
                                    start: Object.assign({}, leftRayIntersection),
                                    end: Object.assign({}, tail),
                                },
                                {
                                    type: 'line',
                                    start: Object.assign({}, tail),
                                    end: Object.assign({}, rightRayIntersection),
                                },
                                {
                                    type: 'arc',
                                    center: Object.assign({}, segment.center),
                                    radius: segment.radius,
                                    angleFrom: rightAngle,
                                    angleTo: segment.angleTo,
                                },
                            ]);
                        }
                        break;
                    case 'line':
                        accum.push(...[
                            {
                                type: 'line',
                                start: Object.assign({}, segment.start),
                                end: Object.assign({}, leftRayIntersection),
                            },
                            {
                                type: 'line',
                                start: Object.assign({}, leftRayIntersection),
                                end: Object.assign({}, tail),
                            },
                            {
                                type: 'line',
                                start: Object.assign({}, tail),
                                end: Object.assign({}, rightRayIntersection),
                            },
                            {
                                type: 'line',
                                start: Object.assign({}, rightRayIntersection),
                                end: Object.assign({}, segment.end),
                            },
                        ]);
                        break;
                    default:
                        break;
                }
            }
            else {
                accum.push(segment);
            }
            return accum;
        }, []);
    }
    // for case when different segment are intersected track segment indices
    const intersectionsMap = new Map();
    intersectionsMap.set(lIntersection.segmentIndex, lIntersection);
    intersectionsMap.set(rIntersection.segmentIndex, rIntersection);
    const segmentsCount = segments.length;
    // traverse all segments starting from arrow left segment (i.e. draw cloud arrow first)
    const segmentsOrdering = new Array(segmentsCount)
        .fill(0)
        .map((_, index) => (lIntersection.segmentIndex + index) % segmentsCount);
    // get arrow closing line intersection index in new traversal ordering
    const rSegmentOrderIndex = segmentsOrdering.findIndex(segmentIndex => rIntersection.segmentIndex === segmentIndex);
    // traverse all segments, excluding segments fully enclosed by arrow intersections
    segmentsOrdering.forEach((segmentIndex, segmentIndexOrder) => {
        const segment = segments[segmentIndex];
        // there can be an segment/s that is cut out because rays are intersecting an segments before and after
        if (segmentIndexOrder > 0 && segmentIndexOrder < rSegmentOrderIndex) {
            return;
        }
        // get intersection
        const intersectionDetails = intersectionsMap.get(segmentIndex);
        // if no intersection with this segment, then draw entire segment
        if (!intersectionDetails) {
            result.push(segment);
            return;
        }
        // if there is an intersection with this segment, divide it and add arrow tail line
        const { intersection } = intersectionDetails;
        let newSegments = [];
        // for each intersecting segment divide it and create a begining or ending of an arrow
        switch (segment.type) {
            case 'arc':
                {
                    const { x: cx, y: cy } = segment.center;
                    const intersectionAngle = Math.atan2(intersection.y - cy, intersection.x - cx);
                    switch (intersectionDetails.type) {
                        case 'start':
                            newSegments = [
                                {
                                    type: 'arc',
                                    center: Object.assign({}, segment.center),
                                    radius: segment.radius,
                                    angleFrom: segment.angleFrom,
                                    angleTo: intersectionAngle,
                                },
                                {
                                    type: 'line',
                                    start: Object.assign({}, intersection),
                                    end: Object.assign({}, tail),
                                },
                            ];
                            break;
                        case 'end':
                            newSegments = [
                                {
                                    type: 'line',
                                    start: Object.assign({}, tail),
                                    end: Object.assign({}, intersection),
                                },
                                {
                                    type: 'arc',
                                    center: Object.assign({}, segment.center),
                                    radius: segment.radius,
                                    angleFrom: intersectionAngle,
                                    angleTo: segment.angleTo,
                                },
                            ];
                            break;
                        default:
                            break;
                    }
                }
                break;
            case 'line':
                switch (intersectionDetails.type) {
                    case 'start':
                        newSegments = [
                            {
                                type: 'line',
                                start: Object.assign({}, segment.start),
                                end: Object.assign({}, intersection),
                            },
                            {
                                type: 'line',
                                start: Object.assign({}, intersection),
                                end: Object.assign({}, tail),
                            },
                        ];
                        break;
                    case 'end':
                        newSegments = [
                            {
                                type: 'line',
                                start: Object.assign({}, tail),
                                end: Object.assign({}, intersection),
                            },
                            {
                                type: 'line',
                                start: Object.assign({}, intersection),
                                end: Object.assign({}, segment.end),
                            },
                        ];
                        break;
                    default:
                        break;
                }
                break;
            default:
                break;
        }
        result.push(...newSegments);
    });
    return result;
};
const getCPoint = (rect, x, y) => {
    const centerVec = Point.from(x, y).subtract(rect);
    const { x: cx, y: cy, width, height } = rect;
    const pad = Math.max(rect.radius * 1.05, 10);
    const maxProjectionX = width * 0.5 - pad;
    const maxProjectionY = height * 0.5 - pad;
    centerVec.normalize(1);
    return {
        x: cx + centerVec.x * maxProjectionX,
        y: cy + centerVec.y * maxProjectionY,
    };
};
const getCPointWithDisorder = (rect, x, y) => {
    const centerVec = Point.from(x, y).subtract(rect);
    const { x: cx, y: cy, width, height } = rect;
    const pad = Math.max(rect.radius * 1.05, 10);
    const maxProjectionX = (width * 0.5 - pad) * 0.95;
    const maxProjectionY = (height * 0.5 - pad) * 0.95;
    const rxv = new Point(1, 0);
    const ryv = new Point(0, -1);
    // project on internal X axis
    const rxp = Math.max(-maxProjectionX, Math.min(rxv.dot(centerVec), maxProjectionX));
    // project on internal Y axis
    const ryp = Math.max(-maxProjectionY, Math.min(ryv.dot(centerVec), maxProjectionY));
    // get projected point inside shape
    const projectedX = rxv.x * rxp + ryv.x * ryp;
    const projectedY = rxv.y * rxp + ryv.y * ryp;
    const xSigma = 0.4 * maxProjectionX;
    const ySigma = 0.4 * maxProjectionY;
    const xSpread = 0.6 * maxProjectionX;
    const ySpread = 0.6 * maxProjectionX;
    const maxAmplitude = 0.8;
    // use normal distribution around center with peak modulated by cos function
    const { cos, E: EulerE, abs } = Math;
    const PI_2 = Math.PI * 0.5;
    // 0.4*abs(cos((x/60-pi/2)))
    const amplitudeX = maxAmplitude * abs(cos(projectedX / xSpread - PI_2));
    const fx = amplitudeX * Math.pow(EulerE, -(Math.pow((projectedX / xSigma), 2)));
    const amplitudeY = maxAmplitude * abs(cos(projectedY / ySpread - PI_2));
    const fy = amplitudeY * Math.pow(EulerE, -(Math.pow((projectedY / ySigma), 2)));
    // add some 'disorder' as vary traction to the center
    const dc = Point.from(0, 0).subtract({ x: projectedX, y: projectedY });
    return {
        x: cx + projectedX + dc.x * fx,
        y: cy + projectedY + dc.y * fy,
    };
};
export function calculateCloudPath(rect, tail) {
    // get cloud tail
    let { x: tailX, y: tailY } = tail;
    // calculate arrow ref point inside rectangle
    const projectedCenter = (Consts.useDistortedProjector ? getCPointWithDisorder : getCPoint)(rect, tailX, tailY);
    // is inside rect (ignore rounded rects)
    const isInsideRect = isPointInRect(rect, tailX, tailY);
    if (isInsideRect) {
        const outsideR = Math.max(rect.width, rect.height) * 0.5;
        const vx = projectedCenter.x - rect.x;
        const vy = projectedCenter.y - rect.y;
        const vl = Math.sqrt(vx * vx + vy * vy);
        const distance = outsideR * 1.2;
        const intersection = getRectIntersection(rect, {
            type: 'line',
            start: { x: projectedCenter.x, y: projectedCenter.y },
            end: {
                x: projectedCenter.x + (vx / vl) * 1000,
                y: projectedCenter.y + (vy / vl) * 1000,
            },
        });
        if (intersection) {
            const ix = intersection.x - rect.x;
            const iy = intersection.y - rect.y;
            const il = Math.sqrt(ix * ix + iy * iy);
            tailX = intersection.x + (ix / il) * 40;
            tailY = intersection.y + (iy / il) * 40;
        }
        else {
            tailX = rect.x + (vx / vl) * distance;
            tailY = rect.y + (vy / vl) * distance;
        }
    }
    // run algorithm
    const projectedSegment = {
        type: 'line',
        start: projectedCenter,
        end: { x: tailX, y: tailY },
    };
    const dx = projectedSegment.end.x - projectedSegment.start.x;
    const dy = projectedSegment.end.y - projectedSegment.start.y;
    const dl = Math.sqrt(dx * dx + dy * dy);
    const projectedSegmentVec = { x: dx / dl, y: dy / dl };
    // left ray
    const r1x = projectedSegmentVec.x * rayLeftMtx[0] +
        projectedSegmentVec.y * rayLeftMtx[2];
    const r1y = projectedSegmentVec.x * rayLeftMtx[1] +
        projectedSegmentVec.y * rayLeftMtx[3];
    const tl = {
        type: 'line',
        start: projectedCenter,
        end: {
            x: rect.x + r1x * dl * 1000,
            y: rect.y + r1y * dl * 1000,
        },
    };
    // right ray
    const r2x = projectedSegmentVec.x * rayRightMtx[0] +
        projectedSegmentVec.y * rayRightMtx[2];
    const r2y = projectedSegmentVec.x * rayRightMtx[1] +
        projectedSegmentVec.y * rayRightMtx[3];
    const tr = {
        type: 'line',
        start: projectedCenter,
        end: {
            x: rect.x + r2x * dl * 1000,
            y: rect.y + r2y * dl * 1000,
        },
    };
    // projection 'fake' ref point to be used instead of center
    // setCPoint(projectedCenter);
    // set all points
    // setPoints([
    //     [{ x, y }, getRectIntersection(rect, projectedSegment)],
    //     [tl.end, getRectIntersection(rect, tl)],
    //     [tr.end, getRectIntersection(rect, tr)],
    // ]);
    return getSegmentIntersetionDivision(rect, Point.from(tail), [tl, tr]);
}
